import { AppThunkAction } from "..";
import { homeownersType, Driver, Coverages, HomeCharacteristics, RateStatus, LineOfBusinessType } from "./state";
import { Vehicle } from "./Vehicle/vehicle";

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

export enum Actions {
  START_QUOTE = 'START_QUOTE',
  PREFILL_QUOTE = 'PREFILL_QUOTE',
  ADD_DRIVER = 'ADD_DRIVER',
  UPDATE_DRIVER = 'UPDATE_DRIVER',
  REMOVE_DRIVER = 'REMOVE_DRIVER',
  ADD_VEHICLE = 'ADD_VEHICLE',
  UPDATE_VEHICLE = 'UPDATE_VEHICLE',
  REMOVE_VEHICLE = 'REMOVE_VEHICLE',
  UPDATE_HOME_CHARACTERISTICS = 'UPDATE_HOME_CHARACTERISTICS',
  UPDATE_COVERAGES = 'UPDATE_COVERAGES',
  UPDATE_RATING = 'UPDATE_RATING',
  UPDATE_IMPORT_FAILURE = 'UPDATE_IMPORT_FAILURE',
  UPDATE_IMPORT_FAILURE_DRIVERS = 'UPDATE_IMPORT_FAILURE_DRIVERS',
  UPDATE_IMPORT_FAILURE_VEHICLES = 'UPDATE_IMPORT_FAILURE_VEHICLES',
  UPDATE_IMPORT_FAILURE_INCIDENTS = 'UPDATE_IMPORT_FAILURE_INCIDENTS',
  UPDATE_IMPORT_FAILURE_VINS = 'UPDATE_IMPORT_FAILURE_VINS',
  UPDATE_IMPORT_FAILURE_LOSSES = 'UPDATE_IMPORT_FAILURE_LOSSES'
}

export interface StartQuote {
  type: Actions.START_QUOTE;
  isPersonalAuto: boolean;
  homeowners: homeownersType;
  fullAddress: string;
  addressState: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  zip: string;
  addressVerified: boolean;
  ref1: string;
  ref2: string;
  ref3: string;
  ref4: string;
  ref5: string;
  agencyLicense: string;
}

export interface PrefillQuote {
  type: Actions.PREFILL_QUOTE;
  isPersonalAuto: boolean;
  homeowners: homeownersType;
  lineOfBusiness: LineOfBusinessType;

  fullAddress: string;
  addressState: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  zip: string;
  addressVerified: boolean;

  drivers: Driver[];
  vehicles: Vehicle[];
  homeCharacteristics: HomeCharacteristics | null;
  coverages: Coverages;

  correlationId: string | null;
  rateStatus: RateStatus;
  quoteId: string;
  hasBeenRated: boolean;

  ref1: string;
  ref2: string;
  ref3: string;
  ref4: string;
  ref5: string;

  agencyLicense: string;

  apiRequestImportKey: string | null;
  maxExceededDriversOnImport: boolean;
  maxExceededVehiclesOnImport: boolean;
  maxExceededLossesOnImport: boolean;
  maxExceededIncidentsOnImport: Record<number, boolean>;
  invalidVinOnImport: Record<number, boolean>;
}

export interface AddDriver {
  type: Actions.ADD_DRIVER;
}

export interface UpdateDriver {
  type: Actions.UPDATE_DRIVER;
  driver: Driver;
}

export interface RemoveDriver {
  type: Actions.REMOVE_DRIVER;
  index: number;
}

export interface AddVehicle {
  type: Actions.ADD_VEHICLE;
}

export interface UpdateVehicle {
  type: Actions.UPDATE_VEHICLE;
  vehicle: Vehicle;
}

export interface RemoveVehicle {
  type: Actions.REMOVE_VEHICLE;
  index: number;
}

export interface UpdateHomeCharacteristics {
  type: Actions.UPDATE_HOME_CHARACTERISTICS;
  homeCharacteristics: HomeCharacteristics;
}

export interface UpdateCoverages {
  type: Actions.UPDATE_COVERAGES;
  coverages: Coverages;
}

export interface UpdateRating {
  type: Actions.UPDATE_RATING;
  correlationId: string | null;
  rateStatus: RateStatus;
}

export interface UpdateImportFailure {
  type: Actions.UPDATE_IMPORT_FAILURE;
  importApiFailure: boolean | null
}

export interface UpdateImportFailureDrivers {
  type: Actions.UPDATE_IMPORT_FAILURE_DRIVERS;
  maxExceededDriversOnImport: boolean
}

export interface UpdateImportFailureVehicles {
  type: Actions.UPDATE_IMPORT_FAILURE_VEHICLES;
  maxExceededVehiclesOnImport: boolean
}

export interface UpdateImportFailureIncidents {
  type: Actions.UPDATE_IMPORT_FAILURE_INCIDENTS;
  driverId: number,
  maxExceededIncidentsOnImport: boolean
}

export interface UpdateImportFailureVins {
  type: Actions.UPDATE_IMPORT_FAILURE_VINS;
  vehicleId: number,
  invalidVin: boolean
}

export interface UpdateImportFailureLosses {
  type: Actions.UPDATE_IMPORT_FAILURE_LOSSES;
  maxExceededLossesOnImport: boolean
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).

export type Action = StartQuote
  | PrefillQuote
  | AddDriver
  | UpdateDriver
  | RemoveDriver
  | AddVehicle
  | UpdateVehicle
  | RemoveVehicle
  | UpdateHomeCharacteristics
  | UpdateCoverages
  | UpdateRating
  | UpdateImportFailure
  | UpdateImportFailureDrivers
  | UpdateImportFailureVehicles
  | UpdateImportFailureIncidents
  | UpdateImportFailureVins
  | UpdateImportFailureLosses

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

var startQuote = (isPersonalAuto: boolean, homeowners: homeownersType, fullAddress: string, addressState: string, addressLine1: string,
  addressLine2: string, city: string, zip: string, addressVerified: boolean, agencyLicense: string): AppThunkAction<StartQuote> => (dispatch, getState) => {
    const appState = getState();

    dispatch({
      type: Actions.START_QUOTE,
      isPersonalAuto: isPersonalAuto,
      homeowners: homeowners,
      fullAddress: fullAddress,
      addressState: addressState,
      addressLine1: addressLine1,
      addressLine2: addressLine2,
      city: city,
      zip: zip,
      addressVerified: addressVerified,

      ref1: appState.startupParameters.ref1 || '',
      ref2: appState.startupParameters.ref2 || '',
      ref3: appState.startupParameters.ref3 || '',
      ref4: appState.startupParameters.ref4 || '',
      ref5: appState.startupParameters.ref5 || '',
      agencyLicense: agencyLicense
    });

  };

var prefillQuote = (isPersonalAuto: boolean, homeowners: homeownersType, lineOfBusiness: LineOfBusinessType, fullAddress: string, addressState: string,
  addressLine1: string, addressLine2: string, city: string, zip: string, addressVerified: boolean, agencyLicense: string, drivers: Driver[],
  vehicles: Vehicle[], homeCharacteristics: HomeCharacteristics, coverages: Coverages, quoteId: string, apiRequestImportKey: string | null,
  maxExceededDriversOnImport: boolean, maxExceededVehiclesOnImport: boolean, maxExceededLossesOnImport: boolean,
  maxExceededIncidentsOnImport: Record<number, boolean>, invalidVinOnImport: Record<number, boolean>): AppThunkAction<PrefillQuote> => (dispatch, getState) => {
    const appState = getState();

    dispatch({
      type: Actions.PREFILL_QUOTE,
      isPersonalAuto: isPersonalAuto,
      homeowners: homeowners,
      lineOfBusiness: lineOfBusiness,
      fullAddress: fullAddress,
      addressState: addressState,
      addressLine1: addressLine1,
      addressLine2: addressLine2,
      city: city,
      zip: zip,
      addressVerified: addressVerified,

      ref1: appState.startupParameters.ref1 || '',
      ref2: appState.startupParameters.ref2 || '',
      ref3: appState.startupParameters.ref3 || '',
      ref4: appState.startupParameters.ref4 || '',
      ref5: appState.startupParameters.ref5 || '',
      agencyLicense: agencyLicense,

      drivers: drivers,
      vehicles: vehicles,
      homeCharacteristics: homeCharacteristics,
      coverages: coverages,

      correlationId: null, 
      rateStatus: RateStatus.NeedsRated, 
      quoteId: quoteId,
      hasBeenRated: false,

      apiRequestImportKey: apiRequestImportKey,
      maxExceededDriversOnImport: maxExceededDriversOnImport,
      maxExceededVehiclesOnImport: maxExceededVehiclesOnImport,
      maxExceededLossesOnImport: maxExceededLossesOnImport,
      maxExceededIncidentsOnImport: maxExceededIncidentsOnImport,
      invalidVinOnImport: invalidVinOnImport
    });
  };

const addDriver = (): AddDriver => ({
  type: Actions.ADD_DRIVER
});

const updateDriver = (driver: Driver): UpdateDriver => ({
  type: Actions.UPDATE_DRIVER,
  driver
});

const removeDriver = (index: number): RemoveDriver => ({
  type: Actions.REMOVE_DRIVER,
  index
});

const addVehicle = (): AddVehicle => ({
  type: Actions.ADD_VEHICLE
});

const updateVehicle = (vehicle: Vehicle): UpdateVehicle => ({
  type: Actions.UPDATE_VEHICLE,
  vehicle
});

const removeVehicle = (index: number): RemoveVehicle => ({
  type: Actions.REMOVE_VEHICLE,
  index
});

const updateHomeCharacteristics = (homeCharacteristics: HomeCharacteristics): UpdateHomeCharacteristics => ({
  type: Actions.UPDATE_HOME_CHARACTERISTICS,
  homeCharacteristics
});

const updateCoverages = (coverages: Coverages): UpdateCoverages => ({
  type: Actions.UPDATE_COVERAGES,
  coverages
});

const updateRating = (correlationId: string | null, rateStatus: RateStatus): UpdateRating => ({
  type: Actions.UPDATE_RATING,
  correlationId,
  rateStatus
});

var updateImportFailure = (importApiFailure: boolean | null): AppThunkAction<UpdateImportFailure> => (dispatch) => {
  dispatch({
    type: Actions.UPDATE_IMPORT_FAILURE,
      importApiFailure
    });
};

var updateImportFailureDrivers = (maxExceededDriversOnImport: boolean): AppThunkAction<UpdateImportFailureDrivers> => (dispatch) => {
  dispatch({
    type: Actions.UPDATE_IMPORT_FAILURE_DRIVERS,
    maxExceededDriversOnImport
  });
};

var updateImportFailureVehicles = (maxExceededVehiclesOnImport: boolean): AppThunkAction<UpdateImportFailureVehicles> => (dispatch) => {
  dispatch({
    type: Actions.UPDATE_IMPORT_FAILURE_VEHICLES,
    maxExceededVehiclesOnImport
  });
};

var updateImportFailureIncidents = (driverId: number, maxExceededIncidentsOnImport: boolean): AppThunkAction<UpdateImportFailureIncidents> => (dispatch) => {
  dispatch({
    type: Actions.UPDATE_IMPORT_FAILURE_INCIDENTS,
    driverId,
    maxExceededIncidentsOnImport
  });
};

var updateImportFailureVins = (vehicleId: number, invalidVin: boolean): AppThunkAction<UpdateImportFailureVins> => (dispatch) => {
  dispatch({
    type: Actions.UPDATE_IMPORT_FAILURE_VINS,
    vehicleId,
    invalidVin
  });
};

var updateImportFailureLosses = (maxExceededLossesOnImport: boolean): AppThunkAction<UpdateImportFailureLosses> => (dispatch) => {
  dispatch({
    type: Actions.UPDATE_IMPORT_FAILURE_LOSSES,
    maxExceededLossesOnImport
  });
};

export const actionCreators = {
  startQuote,
  prefillQuote,
  addDriver,
  updateDriver,
  removeDriver,
  addVehicle,
  updateVehicle,
  removeVehicle,
  updateHomeCharacteristics,
  updateCoverages,
  updateRating,
  updateImportFailure,
  updateImportFailureDrivers,
  updateImportFailureVehicles,
  updateImportFailureIncidents,
  updateImportFailureVins,
  updateImportFailureLosses
};
